/**
 * BottomSheet primitive — Plan 02-10 / UI-SPEC §"<BottomSheet>".
 *
 * Mobile (<1024px): slide-up from bottom; default panel height 60vh.
 * Tablet (≥1024px): centred modal at 480×auto.
 *
 * Behaviour:
 *   • Renders into document.body via React portal so the sheet escapes
 *     nested overflow:hidden parents and any enclosing stacking context. The
 *     portal does NOT put the sheet above everything: it sits at the modal
 *     tier (z-50), which is below IdleMode's ambient takeover (z-60) — the
 *     wall tablet fading to the hub covers an open sheet on purpose, and
 *     reveals it again on wake. See Pattern 7 in docs/ARCHITECTURE.md for the
 *     full ladder before adding a new full-screen surface.
 *   • Esc key + tap on backdrop fire onClose; tap inside the sheet does
 *     NOT (stopPropagation on the panel).
 *   • Focus trap: on open, focus the first focusable child (or the close
 *     button); on close, restore focus to the previously-active element.
 *   • Idle timer suppression — while the sheet is open, `useIdleHold(open)`
 *     takes a reference on the dashboard idle-hold register so the 60s
 *     wall-tablet countdown defers instead of fading the screen out from
 *     under an open sheet (issue #29). The hold is released on close and on
 *     unmount, and is subject to the timer's five-minute deadman ceiling.
 *   • Reduced-motion: matches '(prefers-reduced-motion: reduce)' set
 *     `data-reduced-motion="true"` on the panel; motion.css already zeroes
 *     animation/transition durations under the same query, so the panel
 *     simply appears/disappears without the slide spring.
 *
 * The sheet is intentionally headless beyond a small chrome header (close
 * button + optional title). Consumers (ChoreCompleteSheet, Plan-12 RewardClaimSheet) compose their own body markup as children.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useIdleHold } from '../../lib/useIdleHold';
import { Icon } from './Icon';

interface BottomSheetProps {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  /**
   * Eget topphode i stedet for tittel + X (#157, D9): Hendelse-arket har
   * skissens X · tittel · Lagre øverst. Kallstedet eier da lukkeknappen selv.
   */
  header?: ReactNode;
  /**
   * id-en til overskriften i et eget topphode, som gir dialogen navnet. Med
   * `title` navngis dialogen av tittelen uten at kallstedet gjør noe.
   */
  labelledBy?: string;
  /** Mobile snap point — default 'half' = 60vh; 'full' = 100vh. */
  snapPoints?: 'half' | 'full';
}

/**
 * Skissens topphode for høye ark (#157, D9): X · tittel · handling, festet
 * øverst når arket rulles, så handlingen (Lagre) alltid kan nås. Gis til
 * `header`, med `titleId` også som `labelledBy`. Hendelse- og Middag-arket
 * bruker det.
 *
 * Forskyvningen (`-top-3 -mt-3`) står i stil med panelets `pt-3`, som
 * BottomSheet også beholder på nettbrett når `header` er satt. Endres
 * panelets padding, må topphodet følge med.
 */
export function SheetHeader({
  titleId,
  title,
  onClose,
  close = {},
  action,
}: {
  titleId: string;
  title: ReactNode;
  onClose: () => void;
  /**
   * X-knappen: tilgjengelig navn (standard «Lukk arket»), deaktivert mens
   * arket lagrer, og testid. `testId` er en objektegenskap, så testid-
   * kontrakten ser den bokstavelige verdien hos kallstedet.
   */
  close?: { label?: string; disabled?: boolean; testId?: string };
  action?: ReactNode;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="sticky -top-3 z-10 -mt-3 mb-4 flex items-center gap-3 bg-sheet pt-4 pb-1">
      {/* Sporgrå fyll som i skissene (`t.track`): `bg-card` forsvant nesten
          mot det hvite arket i lyst tema (#159, steg 9g). */}
      <button
        type="button"
        onClick={onClose}
        disabled={close.disabled}
        aria-label={close.label ?? t('bottomSheet.dismiss')}
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-track text-ink"
        data-testid={close.testId}
      >
        <Icon.XMark size={20} />
      </button>
      <h2
        id={titleId}
        className="m-0 min-w-0 flex-1 truncate text-heading-m font-extrabold tracking-tight text-ink"
      >
        {title}
      </h2>
      {action}
    </div>
  );
}

/**
 * Detect prefers-reduced-motion synchronously at render time. Tests
 * override window.matchMedia per case; production gets the live value.
 */
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function BottomSheet({
  open,
  onClose,
  children,
  title,
  header,
  labelledBy,
  snapPoints = 'half',
}: BottomSheetProps): ReactNode {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const previousFocusRef = useRef<Element | null>(null);

  // Idle-timer suppression while open (issue #29). An open sheet is a
  // deliberate, unfinished interaction — the wall tablet must not fade to the
  // ambient hub underneath it just because 60s passed without a pointer event.
  useIdleHold(open);

  // Esc key dismiss: a single document-level keydown listener while open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (ev: globalThis.KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus trap — store the previously-focused element on open, focus the
  // panel (or first focusable child) on open, restore on close.
  useEffect(() => {
    if (!open) return undefined;
    previousFocusRef.current = document.activeElement;

    // Defer one frame so the panel has mounted before we focus it.
    const id = window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelector<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable) {
        focusable.focus();
      } else {
        panel.focus();
      }
    }, 0);

    return () => {
      window.clearTimeout(id);
      const prev = previousFocusRef.current;
      if (prev instanceof HTMLElement) {
        prev.focus();
      }
    };
  }, [open]);

  const onBackdropClick = useCallback(() => {
    onClose();
  }, [onClose]);

  const onPanelKeyDown = useCallback(
    (ev: ReactKeyboardEvent<HTMLDivElement>) => {
      // Tab key — wrap focus inside the panel (cheap focus trap).
      if (ev.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute('disabled'));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (!first || !last) return;
      if (ev.shiftKey && document.activeElement === first) {
        ev.preventDefault();
        last.focus();
      } else if (!ev.shiftKey && document.activeElement === last) {
        ev.preventDefault();
        first.focus();
      }
    },
    [],
  );

  if (!open) return null;
  if (typeof document === 'undefined') return null;

  const reducedMotion = prefersReducedMotion();

  // Arbitrary sheet dimensions are sheet-specific (60vh / 80vh / 480px) —
  // not part of the @theme spacing scale. Inline `style={{ }}` is the
  // project's documented escape hatch for primitive-private dimensions
  // (see PixelAvatar size, WxIcon size, PhotoProofThumb size). Tablet
  // (≥1024px) overrides via a CSS media-query inline style would force
  // a CSS-in-JS dep — the simpler path is two static branches: phone
  // dimensions on the panel by default, lg: utilities zero them out and
  // the inline style wins for height-on-mobile because lg:h-auto comes
  // later in the cascade.

  return createPortal(
    <div
      data-testid="bottom-sheet"
      className="fixed inset-0 z-50 flex items-end justify-center lg:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy ?? (title && !header ? titleId : undefined)}
    >
      {/* Backdrop */}
      <button
        type="button"
        data-testid="bottom-sheet-backdrop"
        aria-label={t('bottomSheet.dismiss')}
        onClick={onBackdropClick}
        className="absolute inset-0 bg-dim"
      />
      {/* Panel */}
      <div
        ref={panelRef}
        data-testid="bottom-sheet-panel"
        data-reduced-motion={reducedMotion ? 'true' : 'false'}
        tabIndex={-1}
        onKeyDown={onPanelKeyDown}
        style={{
          height: snapPoints === 'full' ? '100dvh' : '60vh',
          maxHeight: '80dvh',
          // Tablet override — applied via the responsive class below; on
          // narrower viewports the height: above wins because lg:h-auto
          // comes later in the cascade. The 480px tablet width is sheet-
          // specific (UI-SPEC §"<BottomSheet>"); not part of the spacing
          // scale, so it's expressed inline + overridden in the lg: layer
          // via the panel-tablet selector class (added below).
        }}
        className={[
          // #152: arket fra handoffen — `bg-sheet`, radius 32 øverst, en
          // tynn kantlinje mot innholdet bak og grep-håndtak på telefon.
          'relative w-full bg-sheet text-ink shadow-lg border-t border-card-line',
          'rounded-t-2xl px-5 pt-3 pb-8',
          // Med eget topphode beholder nettbrettet `pt-3`, så SheetHeaders
          // forskyvning (`-top-3 -mt-3`) stemmer der også (#157).
          header ? 'lg:h-auto lg:rounded-2xl lg:border lg:px-6 lg:pb-6 lg:pt-3' : 'lg:h-auto lg:rounded-2xl lg:border lg:p-6',
          'sheet-panel-tablet',
          'transition-transform duration-200',
          'overflow-y-auto',
        ].join(' ')}
        onClick={(ev) => ev.stopPropagation()}
      >
        {/* Grep-håndtaket er pynt: arket lukkes med X, Esc eller scrim. */}
        <span
          aria-hidden
          data-testid="bottom-sheet-handle"
          className="mx-auto mb-3 block h-1.25 w-10 rounded-pill bg-track lg:hidden"
        />
        {/* Header chrome — title + close X, eller kallstedets eget topphode */}
        {header ?? (
          <div className="mb-4 flex items-center justify-between gap-3">
            {title ? (
              <h2 id={titleId} className="text-heading-m font-extrabold text-ink m-0">
                {title}
              </h2>
            ) : (
              <span aria-hidden className="block" />
            )}
            <button
              type="button"
              data-testid="bottom-sheet-close"
              aria-label={t('bottomSheet.dismiss')}
              onClick={onClose}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-track text-ink"
            >
              <Icon.XMark size={20} />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
